import {API_URL, getCookie, handleErrorResponse} from "../utility/utility";

export async function login(email, password) {
    const response = await fetch(`${API_URL}/customer-api/login`, {
        method      : 'POST',
        body        : JSON.stringify({
            email    : email,
            password : password,
        }),
        headers     : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
            'X-XSRF-TOKEN' : getCookie('XSRF-TOKEN'),
        },
        credentials : 'include',
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify({ status: response.status, data: errorData }));
    }

    return response.json();
}

export async function signup(firstName, lastName, email, password, passwordConfirmation, gender) {
    const response = await fetch(`${API_URL}/customer-api/signup`, {
        method      : 'POST',
        body        : JSON.stringify({
            firstName             : firstName,
            lastName              : lastName,
            email                 : email,
            password              : password,
            password_confirmation : passwordConfirmation,
            gender                : gender,
        }),
        headers     : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
            'X-XSRF-TOKEN' : getCookie('XSRF-TOKEN'),
        },
        credentials : 'include',
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify({ status: response.status, data: errorData }));
    }

    return response.json();
}

export async function getAuthenticatedUser() {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/user`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to fetch user');

    return response.json();
}

export async function updateUser(firstName, lastName, phone, birthDate, street, city, zip, oldPassword, newPassword, newPasswordConfirmation) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const data = {};

    if (firstName) {
        data.firstName = firstName;
    }

    if (lastName) {
        data.lastName = lastName;
    }

    if (phone) {
        data.phone = phone;
    }

    if (birthDate) {
        data.birthDate = birthDate;
    }

    if (street) {
        data.street = street;
    }

    if (city) {
        data.city = city;
    }

    if (zip) {
        data.zip = zip;
    }

    if (Object.keys(data).length === 0) {
        return;
    }

    const response = await fetch(`${API_URL}/customer-api/user`, {
        method  : 'PATCH',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify(data)
    });

    if (!response.ok) {
        throw new Error(JSON.stringify({ message: 'Failed to update user', status: 500 }));
    }
}

export async function findGenders() {
    const response = await fetch(`${API_URL}/customer-api/genders`, {
        method : 'GET',
    });

    if (!response.ok) {
        throw new Error(JSON.stringify({ message: 'Failed to fetch genders', status: 500 }));
    }

    return response.json();
}

export async function findEvents(choosedDate, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('date', choosedDate);

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/events?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch events');

    return response.json();
}

export async function findActiveBookingsWithPagination(page, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('page', page);

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/user/active-bookings?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch active bookings');

    return response.json();
}

export async function findPastBookingsWithPagination(page, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('page', page);

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/user/past-bookings?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch past bookings');

    return response.json();
}

export async function findActiveProductsWithPagination(page, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('page', page);

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/user/active-products?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch active products');

    return response.json();
}

export async function findWebshopOrdersWithPagination(page, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('page', page);

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/user/webshop-orders?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch webshop orders');

    return response.json();
}

export async function findWebshopOrderItemsWithPagination(orderUuid, page, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    params.append('page', page);

    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/user/webshop-orders/${orderUuid}/items?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch webshop order items');

    return response.json();
}

export async function cancelBooking(bookingUuid) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/bookings/${bookingUuid}/cancel`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to cancel booking');
}

export async function createBooking(eventUuid) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/events/${eventUuid}/bookings`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to create booking');

    return response.json();
}

export async function getEvent(eventUuid) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/events/${eventUuid}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to fetch event');

    return response.json();
}

export async function findEventProducts(eventUuid) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/events/${eventUuid}/products`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        }
    });

    await handleErrorResponse(response, 'Failed to fetch event products');

    return response.json();
}

export async function getEventBookingOptions(eventUuid, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/events/${eventUuid}/booking-options`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch event booking options');

    return response.json();
}

export async function getBooking(bookingUuid, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/bookings/${bookingUuid}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch booking');

    return response.json();
}

export async function getCheckoutDetails(eventUuid, productUuid, coupon, paymentMethod, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const params = new URLSearchParams();
    if (coupon) {
        params.append('couponCode', coupon);
    }
    if (paymentMethod) {
        params.append('paymentMethod', paymentMethod);
    }
    const queryString = params.toString();

    const response = await fetch(`${API_URL}/customer-api/events/${eventUuid}/products/${productUuid}/checkout-details?${queryString}`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch checkout details');

    return response.json();
}

export async function createPaypalPayment(eventUuid, productUuid, couponCode) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/paypal-create`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            eventUuid   : eventUuid,
            productUuid : productUuid,
            couponCode  : couponCode
        })
    });

    await handleErrorResponse(response, 'Failed to create paypal payment');

    return response.json();
}

export async function capturePaypalPayment(paypalOrderId, payerId, signal = null) {

    const response = await fetch(`${API_URL}/customer-api/paypal-capture`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            paypalOrderId : paypalOrderId,
            payerId       : payerId
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to capture paypal payment');

    return response.json();
}

export async function forgotPassword(email) {
    const response = await fetch(`${API_URL}/customer-api/forgot-password`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            email : email
        })
    });

    await handleErrorResponse(response, 'Failed to send forgot password email');
}

export async function resetPassword(token, password, passwordConfirmation) {
    const response = await fetch(`${API_URL}/customer-api/reset-password`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            token                 : token,
            password              : password,
            password_confirmation : passwordConfirmation
        })
    });

    await handleErrorResponse(response, 'Failed to reset password');
}

export async function createWithdrawal(name, contractReference, email, reason) {
    const response = await fetch(`${API_URL}/customer-api/withdrawals`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            name              : name,
            contractReference : contractReference,
            email             : email,
            reason            : reason || null,
        })
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify({ status: response.status, data: errorData }));
    }

    return response.json();
}

export async function createStripeCheckoutSession(eventUuid, productUuid, couponCode, paymentType) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/stripe/checkout-session`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            eventUuid   : eventUuid,
            productUuid : productUuid,
            paymentType : paymentType,
            couponCode  : couponCode
        })
    });

    await handleErrorResponse(response, 'Failed to create stripe payment');

    return response.json();
}

export async function captureStripePayment(sessionId, microtime, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/stripe/capture`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            checkoutSessionId : sessionId,
            microtime         : microtime
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to capture stripe payment');

    return response.json();
}

export async function getUnattendedAccessTerms(signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/unattended-access-terms`, {
        method  : 'GET',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch unattended access terms');

    return response.json();
}

export async function acceptUnattendedAccessTerms(version) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/unattended-access-terms/accept`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            version : version
        })
    });

    await handleErrorResponse(response, 'Failed to accept unattended access terms');
}

export async function getItemCategories(signal = null) {
    const response = await fetch(`${API_URL}/customer-api/item-categories`, {
        method  : 'GET',
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch item categories');

    return response.json();
}

export async function getItems({page = 1, limit = 20, itemCategoryId = null, itemPropertyValueIds = [], signal = null} = {}) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);

    if (itemCategoryId) {
        params.append('itemCategoryId', itemCategoryId);
    }

    itemPropertyValueIds.forEach((itemPropertyValueId) => {
        params.append('itemPropertyValueIds[]', itemPropertyValueId);
    });

    const response = await fetch(`${API_URL}/customer-api/items?${params.toString()}`, {
        method  : 'GET',
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch items');

    return response.json();
}

export async function getItem(itemUuid, signal = null) {
    const response = await fetch(`${API_URL}/customer-api/items/${itemUuid}`, {
        method  : 'GET',
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch item');

    return response.json();
}

export async function getItemsCheckoutPrice(items, couponCode, paymentMethod, signal = null) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/items/checkout-price`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            items         : items,
            couponCode    : couponCode,
            paymentMethod : paymentMethod
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to fetch checkout price');

    return response.json();
}

export async function createItemsPaypalPayment(items, couponCode) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/items/paypal-create`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            items      : items,
            couponCode : couponCode
        })
    });

    await handleErrorResponse(response, 'Failed to create paypal payment');

    return response.json();
}

export async function createItemsStripeCheckoutSession(items, couponCode, paymentType) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/items/stripe/checkout-session`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            items       : items,
            couponCode  : couponCode,
            paymentType : paymentType
        })
    });

    await handleErrorResponse(response, 'Failed to create stripe payment');

    return response.json();
}

export async function captureItemsPaypalPayment(paypalOrderId, signal = null) {
    const response = await fetch(`${API_URL}/customer-api/items/paypal-capture`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            paypalOrderId : paypalOrderId
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to capture paypal payment');

    return response.json();
}

export async function captureItemsStripePayment(checkoutSessionId, signal = null) {
    const response = await fetch(`${API_URL}/customer-api/items/stripe/capture`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            checkoutSessionId : checkoutSessionId
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to capture stripe payment');

    return response.json();
}

export async function createMolliePayment(eventUuid, productUuid, couponCode) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/mollie-create`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            eventUuid   : eventUuid,
            productUuid : productUuid,
            couponCode  : couponCode
        })
    });

    await handleErrorResponse(response, 'Failed to create mollie payment');

    return response.json();
}

export async function captureMolliePayment(token, signal = null) {
    const response = await fetch(`${API_URL}/customer-api/mollie-capture`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            token : token
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to capture mollie payment');

    return response.json();
}

export async function createItemsMolliePayment(items, couponCode) {
    const token = localStorage.getItem('ACCESS_TOKEN');

    const response = await fetch(`${API_URL}/customer-api/items/mollie-create`, {
        method  : 'POST',
        headers : {
            'Accept'        : 'application/json',
            'Content-Type'  : 'application/json',
            'Authorization' : 'Bearer ' + token
        },
        body    : JSON.stringify({
            items      : items,
            couponCode : couponCode
        })
    });

    await handleErrorResponse(response, 'Failed to create mollie payment');

    return response.json();
}

export async function captureItemsMolliePayment(token, signal = null) {
    const response = await fetch(`${API_URL}/customer-api/items/mollie-capture`, {
        method  : 'POST',
        headers : {
            'Accept'       : 'application/json',
            'Content-Type' : 'application/json',
        },
        body    : JSON.stringify({
            token : token
        }),
        signal  : signal
    });

    await handleErrorResponse(response, 'Failed to capture mollie payment');

    return response.json();
}
